/**
 * Reise for én act (PERFORMANCE.md, fane 4 — Travel).
 *
 * ---------------------------------------------------------------------------------------------
 * TWO LISTS ON SCREEN, ONE SHAPE ON THE WIRE.
 *
 * «Reise inn og ut» and «lokal transport» read differently — a flight has a number and a booking
 * reference, a bus run has a vehicle and a driver — but they are the same thing underneath:
 * something moves a named set of people between two places at two times, and each becomes a line in
 * the timetable. `kind` is what the page splits on.
 *
 * ---------------------------------------------------------------------------------------------
 * PASSENGERS ARE INLINE AND REPLACED WHOLE.
 *
 * A journey without its passengers is a row nobody can act on — the question is always «who is on
 * this», never «is there a bus». So they ride on the DTO, and setting them is one request carrying
 * the whole list rather than an add and a remove per person: the UI is a set of chips, and
 * «everybody who is on it now» is what somebody pressing save means. The same call
 * `setShiftAssignees` made, for the same reason.
 *
 * ---------------------------------------------------------------------------------------------
 * `driver` IS NULLABLE AND ITS ABSENCE IS THE POINT.
 *
 * «Et oppdrag uten sjåfør skal være synlig som mangel, både her og i timetable.» So the DTO carries
 * null rather than omitting the field, and every surface that draws a journey draws the gap.
 */
import { z } from 'zod';

import { travelKindSchema, travelModeSchema } from '../enums.js';
import { contextAllocationSchema } from './resources.js';
import { forWhomSchema, oneOwner, ONE_OWNER } from './logistics-owner.js';
import { rowViewerSchema } from './row-viewer.js';

/** «Ordner transport selv»: selv, kjører selv (artist eller tour manager), ekstern (drosje, buss). */
export const TRANSPORT_ARRANGEMENTS = ['self', 'self_drive', 'external'] as const;
export type TransportArrangement = (typeof TRANSPORT_ARRANGEMENTS)[number];
export const transportArrangementSchema = z
  .enum(TRANSPORT_ARRANGEMENTS)
  .meta({ id: 'TransportArrangement' });

/** How many people one journey may carry. A coach, not a stadium. */
export const MAX_TRAVEL_PASSENGERS = 60;

/** How many journeys one act may have. Two flights and a fortnight of runs, with room to spare. */
export const MAX_TRAVEL_LEGS = 60;

export const MAX_TRAVEL_TEXT = 200;

/** One passenger, as the chip a page draws. The name is resolved; the id is what a write sends. */
export const travelPassengerSchema = z
  .object({
    performancePersonId: z.uuid(),
    displayName: z.string(),
    /** «Vokal», «Tour manager». Null when nobody has given them one on this act. */
    role: z.string().nullable(),
  })
  .meta({ id: 'TravelPassenger' });
export type TravelPassenger = z.infer<typeof travelPassengerSchema>;

export const performanceTravelDtoSchema = z
  .object({
    id: z.uuid(),
    /** Null for a journey that belongs to the year rather than to an act — sak 78.1. */
    performanceId: z.uuid().nullable(),
    editionId: z.uuid(),
    /** «For hvem», free text. What a row without an act is called in a list. */
    forWhom: z.string().nullable(),
    kind: travelKindSchema,
    mode: travelModeSchema,
    fromPlace: z.string().nullable(),
    toPlace: z.string().nullable(),
    /** Instants, rendered in the ÅRGANGENS zone like every other time on this page (vedtak 0016). */
    departsAt: z.iso.datetime().nullable(),
    arrivesAt: z.iso.datetime().nullable(),
    /**
     * DAG UTEN KLOKKE (215.15). Sant: tidspunktet over er midnatt i årgangens sone og klokka er
     * ikke kjent — vis dagen alene. Aldri 00:00 som vakt: en ekte nattreise går 00:00.
     */
    departsDateOnly: z.boolean(),
    arrivesDateOnly: z.boolean(),
    reference: z.string().nullable(),
    bookingReference: z.string().nullable(),
    vehicle: z.string().nullable(),
    /** One of the årgang's own crew. Null here AND below is «mangler sjåfør» — see the header. */
    driverEditionPersonId: z.uuid().nullable(),
    /**
     * Eller en kontakt som er med på festivalen uten å stå i staben — artisten, tour manageren
     * (215.20). Høyst én av de to er satt.
     */
    driverContactId: z.uuid().nullable(),
    /** Navnet: den på sjåførvakta, ellers en av de to over. */
    driverName: z.string().nullable(),
    /**
     * SJÅFØREN ER EN VAKT (253.34–253.37, 0925). Vakta på posten «Sjåfør», festet til bilen i
     * bilens tidsrom. Er den satt, er sjåføren den som står på den, og de to kolonnene over er tomme.
     */
    driverShift: z
      .object({
        id: z.uuid(),
        startsAt: z.iso.datetime(),
        endsAt: z.iso.datetime(),
        personId: z.uuid().nullable(),
      })
      .nullable()
      .optional(),
    /** «Ordner transport selv» (253.34–253.35). Null: festivalen kjører, og en sjåfør trengs. */
    transportArrangement: transportArrangementSchema.nullable().optional(),
    transportNote: z.string().nullable().optional(),
    /** Lokal transport som festivalen kjører, uten sjåfør. Det varselet «Mangler sjåfør» teller. */
    needsDriver: z.boolean().optional(),
    notes: z.string().nullable(),
    /** Who is on it, whole. See the header for why they are not a separate request. */
    passengers: z.array(travelPassengerSchema),
    /**
     * RESSURSEN REISEN ER BOOKET MOT, når den er det (spesifikasjonen §6, trinn 2).
     *
     * Null er det vanlige og det riktige for alt som lå her før: da gjelder `vehicle`-friteksten.
     * Er den satt, viser skjermen ressursens navn og skjuler friteksten — de to er to svar på «hva
     * gjør det her», og å vise begge er å invitere til at de blir uenige (analysen §3.7).
     *
     * Den er en SETNING og ikke et objekt med typens felt: byrået leser den samme raden, og det de
     * skal se er «Bil 2, 16:00» — ikke reg.nr. Å ta med felt her ville betydd at DTO-en måtte
     * skjule dem igjen ett lag lenger ute.
     */
    allocation: contextAllocationSchema.nullable(),
    /** Hva leseren kan gjøre med raden (D1), regnet av `can()` på serveren. */
    viewer: rowViewerSchema.optional(),
  })
  .meta({ id: 'PerformanceTravelDto' });
export type PerformanceTravelDto = z.infer<typeof performanceTravelDtoSchema>;

/**
 * Not paged: `MAX_TRAVEL_LEGS` bounds it, and a page of a travel plan is a travel plan with a leg
 * missing — which on the day is how somebody ends up at an airport with no bus.
 */
export const performanceTravelListResponseSchema = z
  .object({ items: z.array(performanceTravelDtoSchema) })
  .meta({ id: 'PerformanceTravelListResponse' });
export type PerformanceTravelListResponse = z.infer<typeof performanceTravelListResponseSchema>;

export const listPerformanceTravelQuerySchema = z
  .object({
    performanceId: z.uuid(),
    /** Absent means every journey, which is what the page asks for. */
    kind: travelKindSchema.optional(),
  })
  .meta({ id: 'ListPerformanceTravelQuery' });
export type ListPerformanceTravelQuery = z.infer<typeof listPerformanceTravelQuerySchema>;

const place = z.string().trim().min(1).max(MAX_TRAVEL_TEXT);

/**
 * FLIGHT NUMBER AND BOOKING REFERENCE ARE STORED UPPER-CASED (Atle, 2. september: «Nummer og
 * referanse kan komme i Caps lock»).
 *
 * A PNR is printed in capitals on every ticket and read out in capitals on every phone call, and
 * `sk4108` beside `SK4108` in the same column reads as two different flights. The transform is on
 * the CONTRACT rather than in the form, so a value typed in a browser and one sent by a future
 * mobile client are stored identically — normalising in one client only is how the two spellings
 * get back in.
 *
 * `toUpperCase()` and not `toLocaleUpperCase()`: these are codes in the Latin alphabet, and the
 * Turkish dotless i would turn `i` into `İ` in a reference nobody can then quote back.
 */
const code = place.transform((value) => value.toUpperCase());

/**
 * The arrival cannot be before the departure (Atle, 2. september).
 *
 * `>=` and not `>`: a local run across the site is entered with one clock, and a leg that departs
 * and arrives at the same minute is a five-hundred-metre transfer rather than a mistake. The
 * database says the same thing in `ck_performance_travel__time_order`, and that is the layer that
 * cannot be gone round — this one is what turns the refusal into a 400 naming `arrivesAt`.
 *
 * `Date.parse` rather than a string comparison: `z.iso.datetime()` accepts both `…:00Z` and
 * `…:00.000Z`, and those two sort against each other on the wrong character.
 */
const inTimeOrder = (body: {
  departsAt?: string | null | undefined;
  arrivesAt?: string | null | undefined;
  departsDateOnly?: boolean | undefined;
  arrivesDateOnly?: boolean | undefined;
}): boolean =>
  body.departsAt == null ||
  body.arrivesAt == null ||
  /* En dag uten klokke sammenlignes som DATO i årgangens sone, og sonen kjenner bare tjenesten. */
  body.departsDateOnly === true ||
  body.arrivesDateOnly === true ||
  Date.parse(body.arrivesAt) >= Date.parse(body.departsAt);

/**
 * «DATO UTEN KLOKKESLETT» (215.15). Sant betyr at tidspunktet bare bærer en DAG: tjenesten flytter
 * det til midnatt den dagen i årgangens sone, så et hvilket som helst øyeblikk den dagen er godt nok.
 * Usendt på en PATCH betyr uendret; et nytt tidspunkt uten flagget er et tidspunkt MED klokke.
 */
const dateOnlyFlag = z.boolean().optional();

const TIME_ORDER = {
  message: 'The arrival cannot be before the departure.',
  path: ['arrivesAt'],
};

/**
 * HØYST ÉN SJÅFØR (215.20, migrering 0382). Stabsraden eller kontakten, som
 * `ck_performance_travel__one_driver` sier — her blir det en 400 på feltet i stedet for en 23514.
 */
const oneDriver = (body: {
  driverEditionPersonId?: string | null | undefined;
  driverContactId?: string | null | undefined;
}): boolean => body.driverEditionPersonId == null || body.driverContactId == null;

const ONE_DRIVER = {
  message: 'A journey has one driver.',
  path: ['driverContactId'],
};

/**
 * «SETT SJÅFØR» (253.34, 253.37). Personen settes på sjåførvakta til reisen; finnes den ikke, lages
 * den på posten «Sjåfør» (med `postName` fra språkfila) i bilens avdeling og bilens tidsrom.
 */
export const setTravelDriverRequestSchema = z
  .object({
    personId: z.uuid(),
    postName: z.string().trim().min(1).max(80),
    /**
     * «Lag vakt automatisk» (A15, 271.4 a). Standard ja. Nei: ingen vakt lages; har personen alt en
     * sjåførvakt på bilen i tidsrommet, brukes den, ellers står personen som sjåfør uten vakt.
     */
    makeShift: z.boolean().optional(),
    /**
     * Avdelingen vakta lages i når bilen ikke har en hovedavdeling i årgangen. En vakt lages aldri
     * uten avdeling (271.4 a): uten denne og uten bilens svarer API-et `department_required`.
     */
    departmentId: z.uuid().nullish(),
  })
  .meta({ id: 'SetTravelDriverRequest' });
export type SetTravelDriverRequest = z.infer<typeof setTravelDriverRequestSchema>;

export const createPerformanceTravelRequestSchema = z
  .object({
    /** The act this journey belongs to, or null for one that belongs to the year. */
    performanceId: z.uuid().nullish(),
    /** The year, named directly only when there is no act. See `oneOwner`. */
    editionId: z.uuid().nullish(),
    /** «Lydcrew», «fotograf NRK». Free text, and allowed on a row that does have an act too. */
    forWhom: forWhomSchema.nullish(),
    kind: travelKindSchema,
    mode: travelModeSchema,
    fromPlace: place.nullish(),
    toPlace: place.nullish(),
    departsAt: z.iso.datetime().nullish(),
    arrivesAt: z.iso.datetime().nullish(),
    departsDateOnly: dateOnlyFlag,
    arrivesDateOnly: dateOnlyFlag,
    reference: code.nullish(),
    bookingReference: code.nullish(),
    vehicle: place.nullish(),
    driverEditionPersonId: z.uuid().nullish(),
    driverContactId: z.uuid().nullish(),
    notes: z.string().trim().min(1).max(2000).nullish(),
    /**
     * Optional on create, so a leg can be entered before anybody knows who is on it — which is the
     * ordinary order: the flight is booked, then the names are confirmed.
     */
    passengerIds: z.array(z.uuid()).max(MAX_TRAVEL_PASSENGERS).optional(),
  })
  .refine(inTimeOrder, TIME_ORDER)
  .refine(oneDriver, ONE_DRIVER)
  .refine(oneOwner, ONE_OWNER)
  .meta({ id: 'CreatePerformanceTravelRequest' });
export type CreatePerformanceTravelRequest = z.infer<typeof createPerformanceTravelRequestSchema>;

/**
 * `kind` and `performanceId` never move. A local run that became a flight is a different journey,
 * and moving a leg between acts would take its passengers — who are rows on the OTHER act — with
 * it. Both are a delete and a create, which is what they are.
 *
 * THE TIME-ORDER REFINEMENT HERE ONLY SEES THE PATCH, which is half the rule. A request carrying
 * an `arrivesAt` alone is in order as far as this schema can tell, and it is the service that
 * merges it with the stored `departs_at` before writing — see `PerformanceTravelService`.
 */
export const updatePerformanceTravelRequestSchema = z
  .object({
    mode: travelModeSchema.optional(),
    fromPlace: place.nullish(),
    toPlace: place.nullish(),
    departsAt: z.iso.datetime().nullish(),
    arrivesAt: z.iso.datetime().nullish(),
    departsDateOnly: dateOnlyFlag,
    arrivesDateOnly: dateOnlyFlag,
    reference: code.nullish(),
    bookingReference: code.nullish(),
    vehicle: place.nullish(),
    driverEditionPersonId: z.uuid().nullish(),
    driverContactId: z.uuid().nullish(),
    notes: z.string().trim().min(1).max(2000).nullish(),
    /** «For hvem». Editable, unlike the owner above it — a label is not an identity. */
    forWhom: forWhomSchema.nullish(),
    /** «Ordner transport selv» og notatet. Et notat alene fjerner ikke varselet. */
    transportArrangement: transportArrangementSchema.nullish(),
    transportNote: z.string().trim().min(1).max(500).nullish(),
    /**
     * «ORDNER TRANSPORT SELV» MED VAKTA FJERNET (256.13). Sammen med `transportArrangement`: reisen
     * slipper sjåførvakta, og vakta slettes fra vaktplanen når ingen annen reise står på den.
     * Krever `shift:delete`. Uten feltet står vakta igjen, som før.
     */
    removeDriverShift: z.boolean().optional(),
  })
  .refine(inTimeOrder, TIME_ORDER)
  .refine(oneDriver, ONE_DRIVER)
  .refine((body) => body.removeDriverShift !== true || body.transportArrangement != null, {
    message: 'The driver shift is removed together with a transport arrangement.',
    path: ['removeDriverShift'],
  })
  .meta({ id: 'UpdatePerformanceTravelRequest' });
export type UpdatePerformanceTravelRequest = z.infer<typeof updatePerformanceTravelRequestSchema>;

/**
 * REPLACE THE WHOLE LIST, never add or remove one.
 *
 * The UI is a set of chips with an × on each, and «everybody who is on it now» is what somebody
 * pressing save means. A per-person endpoint would make two people editing the same bus produce a
 * union of their intentions rather than the later one's — and a union is not a decision anybody
 * made.
 */
export const setTravelPassengersRequestSchema = z
  .object({ passengerIds: z.array(z.uuid()).max(MAX_TRAVEL_PASSENGERS) })
  .meta({ id: 'SetTravelPassengersRequest' });
export type SetTravelPassengersRequest = z.infer<typeof setTravelPassengersRequestSchema>;
